import React, { useState } from "react";
import classNames from "classnames";
import ResponsiveImage from "../../Shared/ResponsiveImage/responsive-image";
import TagComponent from "../../Shared/Tag/tag";
import ArrowLink from "../../Shared/ArrowLink/arrow-link";
import CustomPrimaryCtaButton from "../../Shared/CustomPrimaryCtaButton/custom-primary-cta-button";
import PropTypes from "prop-types";
import { useElementSize } from "../../../hooks/useElementSize";
import { kebabCase } from "js/utils/string";

export const tileType = {
    COL__1: "col-1",
    COL__2: "col-2",
    COL__4: "col-4"
};

const tileTypes = {
    [tileType.COL__1]: {
        // Size 423px is needed for desktop, since col-1 can be both 25% and 33% wide
        srcSetSizes: [607, 402, 518, 423],
        sizes: `(max-width: 639px) 607px, (max-width: 867px) 402px, (max-width: 1099px) 518px, 423px`
    },
    [tileType.COL__2]: {
        // Size 867px is needed since template 3 and 4 show col-2 in 100% on this breakpoint
        srcSetSizes: [607, 835, 518, 651],
        sizes: `(max-width: 639px) 607px, (max-width: 867px) 835px, (max-width: 1099px) 518px, 651px`
    },
    [tileType.COL__4]: {
        srcSetSizes: [607, 835, 671, 838],
        sizes: "(max-width: 639px) 607px, (max-width: 867px) 835px, (max-width: 1099px) 671px, 838px"
    }
};

const TeaserTile = ({
    ImageSrc,
    ImageAltText,
    Tag,
    Title,
    Trumpet,
    RichText,
    CtaUrl,
    CtaTarget,
    CtaLink,
    ShowCtaAsLink,
    UseCtaAsImageLink,
    tileType: tileTypeFromProps,
    VideoUrl,
    setVideoModal,
    parentAnchor,
    VideoAnchorId,
    focused
}) => {
    const [richTextExpanded, setRichTextExpanded] = useState(false);
    const tileType = tileTypeFromProps || tileType.COL__1;
    const baseClass = "teaser-tile";
    const deepLinkId = parentAnchor && VideoAnchorId ? `${parentAnchor}+video-${VideoAnchorId}` : "";
    const rootClasses = classNames(
        baseClass,
        {
            "teaser-tile--focused": focused
        },
        [`${baseClass}--tile-type-${tileType}`]
    );

    const { height, ref: richTextRef } = useElementSize();

    const showRichTextExpandButton = height < richTextRef.current?.scrollHeight || richTextExpanded;

    const innerClasses = classNames(`${baseClass}__inner`, {
        [`${baseClass}__inner--expanded`]: richTextExpanded,
        [`${baseClass}__inner--expand-disabled`]:
            height === richTextRef.current?.scrollHeight && !richTextExpanded
    });

    return (
        <div className={rootClasses} id={deepLinkId}>
            {ImageSrc && !VideoUrl && (
                <a className={`${baseClass}__image-wrapper`} href={CtaUrl} target={CtaTarget}>
                    <div className={`${baseClass}__image-container`}>
                        <ResponsiveImage
                            srcUrl={ImageSrc}
                            defaultSize={639}
                            srcSetSizes={tileTypes[tileType].srcSetSizes}
                            sizes={tileTypes[tileType].sizes}
                            alt={ImageAltText}
                        />
                    </div>
                </a>
            )}
            {ImageSrc && VideoUrl && (
                <div className={`${baseClass}__image-wrapper ${baseClass}__video-wrapper`}>
                    <div className={`${baseClass}__image-container`}>
                        <a
                            className={`${baseClass}__interactive`}
                            href={`#${deepLinkId}`}
                            onClick={e => {
                                history.pushState(null, null, "#" + deepLinkId);
                                e.preventDefault();
                                setVideoModal(true, VideoUrl);
                            }}
                        >
                            <ResponsiveImage
                                srcUrl={ImageSrc}
                                defaultSize={639}
                                srcSetSizes={tileTypes[tileType].srcSetSizes}
                                sizes={tileTypes[tileType].sizes}
                                alt={ImageAltText}
                            />
                        </a>
                    </div>
                </div>
            )}
            <div className={`${baseClass}__content-wrapper`}>
                <div className={`${baseClass}__content-container`}>
                    <div className={innerClasses}>
                        {Tag && <TagComponent className={`${baseClass}__tag`}>{Tag}</TagComponent>}
                        {Trumpet && <p className={`${baseClass}__subtitle`}>{Trumpet}</p>}
                        {Title && (
                            <a className={`${baseClass}__title-link`} href={CtaUrl} target={CtaTarget}>
                                <h3
                                    className={`${baseClass}__title h3-style`}
                                    dangerouslySetInnerHTML={{ __html: Title }}
                                />
                            </a>
                        )}
                        {RichText && (
                            <div className={`${baseClass}__rich-text`} ref={richTextRef}>
                                {!showRichTextExpandButton ? (
                                    <span dangerouslySetInnerHTML={{ __html: RichText }} />
                                ) : (
                                    <button
                                        type="button"
                                        aria-controls={`${kebabCase(Title)}`}
                                        aria-expanded={richTextExpanded}
                                        className={`${baseClass}__rich-text-button`}
                                        onClick={() => setRichTextExpanded(!richTextExpanded)}
                                    >
                                        <span
                                            id={`${kebabCase(Title)}`}
                                            dangerouslySetInnerHTML={{ __html: RichText }}
                                        />
                                    </button>
                                )}
                            </div>
                        )}
                    </div>
                    {CtaLink && !UseCtaAsImageLink && ShowCtaAsLink && (
                        <ArrowLink ClassName="teaser-tile__arrow-link" CtaLink={CtaLink} />
                    )}
                    {CtaLink && !UseCtaAsImageLink && !ShowCtaAsLink && (
                        <CustomPrimaryCtaButton ClassName="teaser-tile__button" CtaLink={CtaLink} />
                    )}
                </div>
            </div>
        </div>
    );
};

TeaserTile.propTypes = {
    ImageSrc: PropTypes.string,
    ImageAltText: PropTypes.string,
    Tag: PropTypes.string,
    Title: PropTypes.string, // Recommended max: 28 characters
    Trumpet: PropTypes.string, // Recommended max: 30 characters
    RichText: PropTypes.string, // Recommended max: 262 characters
    CtaLink: PropTypes.string,
    CtaUrl: PropTypes.string,
    CtaTarget: PropTypes.string,
    ShowCtaAsLink: PropTypes.bool,
    UseCtaAsImageLink: PropTypes.bool,
    TwentyThreeVideoDomain: PropTypes.string,
    TwentyThreeVideoPlayerId: PropTypes.string,
    parentAnchor: PropTypes.string,
    VideoAnchorId: PropTypes.string,
    focused: PropTypes.bool,
    tileType: PropTypes.oneOf(Object.keys(tileType).map(key => tileType[key]))
};

export default TeaserTile;
